import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { MatPaginator, MatSort } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { debounceTime } from 'rxjs/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/operators/distinctUntilChanged';
import { first } from 'rxjs/operators/first';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { map } from 'rxjs/operators/map';
import { merge } from 'rxjs/observable/merge';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

import { isBefore, isDate, isValid } from 'date-fns';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import { ApplicationService, SchoolInfo } from '../../services/application/application.service';
import { ErrorService } from '../../services/error/error.service';
import { UserService, UserInfo } from '../../services/user/user.service';

@Component({
    selector: 'app-college-student-search',
    styleUrls: ['collegestudentsearch.component.css'],
    templateUrl: 'collegestudentsearch.component.html',
})
export class CollegeStudentSearchComponent implements OnInit, OnDestroy {
    @ViewChild('colstudentidinput') colstudentidinput: ElementRef;
    @ViewChild('firstinput') firstinput: ElementRef;
    @ViewChild('lastinput') lastinput: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild('studentidinput') studentidinput: ElementRef;
    @ViewChild(MatSort) sorter: MatSort;

    private _subRelease: Subject<void> = new Subject<void>();

    // Class
    AllHeaders: string[] = ['CollegeStudentID', 'StudentID', 'LastName', 'FirstName', 'MiddleName', 'Grade', 'Actions'];
    AvailableSchools: SchoolInfo[];
    AvailableSchoolGrades: string[] = ['All', '06', '07', '08', '09', '10', '11', '12'];
    HasAnyUpcoming: boolean;
    LastSizeCode: string;
    PartialViewReady: boolean;
    SearchConducted: boolean;
    SearchData: DataTableSource<SearchResult>;
    SearchForm: FormGroup;
    SearchResults: SearchResult[];
    SelectedSchoolYear: string;
    ShowSearch: boolean;
    SmallDisplay: boolean;
    User: UserInfo;
    VisibleColumns: string[];
    VisibleHeaders: string[];

    constructor(
        private appService: ApplicationService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private mediaChange: ObservableMedia,
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService
    ) {}

    ngOnDestroy() {
        this._subRelease.next();
        this._subRelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AvailableSchools = null;
        self.HasAnyUpcoming = false;
        self.PartialViewReady = false;
        self.SearchConducted = false;
        self.SearchResults = null;
        self.SelectedSchoolYear = null;
        self.ShowSearch = true;

        self.LastSizeCode = self.mediaChange.isActive('xs')
            ? 'xs'
            : self.mediaChange.isActive('sm')
            ? 'sm'
            : self.mediaChange.isActive('md')
            ? 'md'
            : self.mediaChange.isActive('lg')
            ? 'lg'
            : 'xl';
        self.SmallDisplay = self.LastSizeCode === 'xs' || self.LastSizeCode === 'sm';
        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
        self.VisibleColumns = self.SearchResults && self.SearchResults.length ? self.VisibleHeaders.slice() : ['NoData'];

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subRelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.LastSizeCode = media.mqAlias;
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.SearchResults && self.SearchResults.length ? self.VisibleHeaders.slice() : ['NoData'];
            });

        self.SearchData = new DataTableSource<SearchResult>([], self.paginator, self.sorter, self.SortStudents.bind(self));

        self.SearchForm = self.fb.group(
            {
                FirstName: [''],
                LastName: [''],
                StudentID: [''],
                Grade: [''],
                CollegeID: [null],
                CollegeStudentID: [''],
                Term: [''],
                SchoolNumber: [''],
            },
            {
                validator: function(ctrl: FormGroup) {
                    if (!ctrl) {
                        return null;
                    }

                    const form = ctrl.value;

                    if (
                        !form.FirstName &&
                        !form.LastName &&
                        !form.StudentID &&
                        !form.Grade &&
                        !form.CollegeID &&
                        !form.CollegeStudentID &&
                        !form.Term &&
                        !form.SchoolNumber
                    ) {
                        return { noneprovided: true };
                    }
                    if (
                        !form.StudentID &&
                        !form.Grade &&
                        !form.CollegeID &&
                        !form.CollegeStudentID &&
                        !form.Term &&
                        !form.SchoolNumber
                    ) {
                        // Check to make sure we have at least 5 characters combined from the firstname and lastname fields
                        const fn = (form.FirstName || '').length;
                        const ln = (form.LastName || '').length;

                        if (fn + ln < 5) {
                            return { TooFewCharacters: true };
                        }
                    }

                    return null;
                },
            }
        );

        // Wait for a first non-null User result before kicking off the page
        self.userService.UserSubject.pipe(
            takeUntil(self._subRelease),
            first(item => item && !!item.AuthToken)
        ).subscribe(_user => {
            self.User = _user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subRelease)).subscribe(_u => {
                self.User = _u;
            });

            // Hook into items which can change (such as parameters)
            combineLatest(self.appService.SelectedSchoolYearSubject, self.appService.GetAllSchools(self.User.AuthToken), function(
                selYear,
                schools
            ) {
                return {
                    SelectedYear: selYear,
                    Schools: schools,
                };
            })
                .pipe(takeUntil(self._subRelease))
                .subscribe(
                    packet => {
                        self.SelectedSchoolYear = packet.SelectedYear;
                        self.AvailableSchools = packet.Schools;

                        // If this college person only has one possible college to search over, preselect it.
                        if (self.User && self.User.StaffColleges && self.User.StaffColleges.length === 1) {
                            self.SearchForm.patchValue({
                                CollegeID: self.User.StaffColleges[0].CollegeID,
                            });
                        }

                        this.firstinput.nativeElement.focus();

                        // Set the page as ready
                        self.PartialViewReady = true;
                    },
                    error => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    // #region Clear Form Method

    ClearForm() {
        this.SearchForm.reset();

        this.firstinput.nativeElement.focus();
    }

    // #endregion

    // #region Handle Search Method

    HandleSearch() {
        const self = this;

        // If the college choice is -1 (All), null it out before continuing to the search.
        const searchParams = self.SearchForm.value;

        if (searchParams.CollegeID === -1) {
            searchParams.CollegeID = null;
        }

        // Call to get the search results ('0937' - test school)
        self.appService
            .SearchForCollegeStudent(
                self.User.AuthToken,
                self.SelectedSchoolYear,
                searchParams.StudentID,
                searchParams.CollegeID,
                searchParams.CollegeStudentID,
                searchParams.Term,
                self.SelectedSchoolYear,
                searchParams.Grade,
                searchParams.FirstName,
                searchParams.LastName,
                searchParams.SchoolNumber
            )
            .subscribe(
                results => {
                    self.SearchResults = results;
                    if (self.SmallDisplay) {
                        self.ShowSearch = false;
                    }

                    if (self.SearchResults && self.SearchResults.length) {
                        self.VisibleColumns = self.VisibleHeaders.slice();
                    } else {
                        self.VisibleColumns = ['NoData'];
                    }

                    self.SearchData.Update(self.SearchResults);
                    self.SearchConducted = true;
                },
                error => {
                    self.errorService.HandleServiceError(error);
                }
            );
    }

    // #endregion

    // #region Show Search Form Method

    ShowSearchForm() {
        this.ShowSearch = true;
        this.SearchConducted = false;
    }

    // #endregion

    // #region Sort Students Method

    SortStudents(First: SearchResult, Second: SearchResult) {
        const self = this;

        let PropA: string | number | Date;
        let PropB: string | number | Date;

        switch (self.sorter.active) {
            case 'CollegeStudentID':
                [PropA, PropB] = [First.CollegeStudentID, Second.CollegeStudentID];
                break;
            case 'StudentID':
                [PropA, PropB] = [First.StudentID, Second.StudentID];
                break;
            case 'LastName':
                [PropA, PropB] = [First.LastName, Second.LastName];
                break;
            case 'FirstName':
                [PropA, PropB] = [First.FirstName, Second.FirstName];
                break;
            case 'MiddleName':
                [PropA, PropB] = [First.MiddleName, Second.MiddleName];
                break;
            case 'Grade':
                [PropA, PropB] = [First.GradeLevel, Second.GradeLevel];
                break;
        }

        if (isDate(PropA)) {
            if (!isDate(PropB)) {
                return 0;
            }
            if (!isValid(<Date>PropA) || !isValid(<Date>PropB)) {
                return 0;
            }

            if (isBefore(PropA, PropB)) {
                return -1 * (self.sorter.direction === 'asc' ? 1 : -1);
            }
            if (isBefore(PropB, PropA)) {
                return 1 * (self.sorter.direction === 'asc' ? 1 : -1);
            }

            return 0;
        } else {
            const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
            const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

            return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
        }
    }

    // #endregion

    // #region View Student Method

    ViewStudent(student: SearchResult) {
        if (!student) {
            return;
        }

        // Route to the student view
        this.router.navigate(['/studentview', student.StudentID]);
    }

    // #endregion
}

// #region Custom Classes

// #region Search Result Class

export class SearchResult {
    FirstName?: string;
    MiddleName?: string;
    LastName?: string;
    StudentID?: string;
    GradeLevel?: string;
    CollegeID?: number;
    CollegeStudentID?: string;
    Term?: string;
    SchoolNumber?: string;
    UpcomingSchoolYear?: boolean;
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/app/collegestudentsearch/collegestudentsearch.component.ts